import React, { useCallback, useEffect, useState } from "react";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";
import TemplateEditor from "../components/TemplateEditor";
import { Empty, Spin, Tag, Typography } from "antd";

const { Title, Text } = Typography;

const EmailTemplateManager = () => {
  //----------- Constants-----------
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const { request } = useRequest();

  // ---------- Handlers -----------
  const fetchAllTemplates = useCallback(() => {
    setLoading(true);
    request({
      url: `/api/admin/email-template`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let templateData = [];
        if (data?.data) {
          if (Array.isArray(data.data)) {
            templateData = data.data;
          }
        } else if (Array.isArray(data)) {
          templateData = data;
        }

        setTemplates(Array.isArray(templateData) ? templateData : []);

        // If we had a selected template, try to find the updated version
        if (selectedTemplate) {
          const updatedTemplate = templateData.find(
            (t) => t.id === selectedTemplate.id
          );
          if (updatedTemplate) {
            setSelectedTemplate(updatedTemplate);
          }
        }
      },
      onError: (error) => {
        setLoading(false);
        setTemplates([]);
        ShowToast("Failed to fetch email templates", Severty.ERROR);
      },
    });
  }, [selectedTemplate]);

  //   --------- Use Effects -----------
  useEffect(() => {
    fetchAllTemplates();
  }, []);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  return (
    <div className="email-template-manager">
      <div className="email-template-shell">
        <div className="template-list-panel">
          <div className="email-card">
            <div className="email-card-header">
              <Title level={4} style={{ margin: 0 }}>
                Email Templates
              </Title>
              <Text type="secondary">Select a template to edit</Text>
            </div>
            <div className="email-card-body">
              {loading ? (
                <div className="email-loading">
                  <Spin size="large" />
                </div>
              ) : (
                <div className="template-list">
                  {templates.length > 0 ? (
                    templates.map((tpl) => (
                      <div
                        key={tpl.id}
                        className={`template-item ${
                          selectedTemplate?.id === tpl.id ? "active" : ""
                        }`}
                        onClick={() => handleTemplateSelect(tpl)}
                      >
                        <div className="template-item-head">
                          <h5 className="template-item-title">{tpl.template_key}</h5>
                          {selectedTemplate?.id === tpl.id && (
                            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                              Selected
                            </Tag>
                          )}
                        </div>
                        <p className="template-item-text">{tpl.subject}</p>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Click to customize content and placeholders
                        </Text>
                      </div>
                    ))
                  ) : (
                    <div className="email-empty">
                      <Empty description="No templates found" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="template-editor-panel">
          <div className="email-card">
            <div className="email-card-header">
              <Title level={4} style={{ margin: 0 }}>
                Template Editor
              </Title>
              {selectedTemplate ? (
                <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
                  {selectedTemplate.template_key}
                </Tag>
              ) : null}
            </div>
            <div className="email-card-body">
              {selectedTemplate ? (
                <TemplateEditor
                  template={selectedTemplate}
                  onUpdate={fetchAllTemplates}
                />
              ) : (
                <div className="template-editor-empty">
                  <Empty
                    description={
                      <div>
                        <h4 style={{ marginBottom: 4 }}>Select a template to edit</h4>
                        <p className="text-muted" style={{ marginBottom: 0 }}>
                          Choose a template from the left panel and start editing.
                        </p>
                      </div>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateManager;
